import { css } from 'lit';

// ---------------------------------------------------------------------------
// Shared R5-token CSS template literals for sysml-mfe components
// ---------------------------------------------------------------------------

export const baseStyles = css`
  :host {
    display: block;
    font-family: var(--r5-font-sans);
    font-size: var(--r5-text-xs);
    color: var(--r5-text);
    box-sizing: border-box;
  }

  *,
  *::before,
  *::after {
    box-sizing: inherit;
  }
`;

export const layoutStyles = css`
  .flex { display: flex; }
  .flex-col { display: flex; flex-direction: column; }
  .flex-1 { flex: 1; min-width: 0; min-height: 0; }
  .flex-shrink-0 { flex-shrink: 0; }
  .items-center { align-items: center; }
  .items-start { align-items: flex-start; }
  .items-stretch { align-items: stretch; }
  .justify-between { justify-content: space-between; }
  .justify-center { justify-content: center; }
  .justify-end { justify-content: flex-end; }
  .gap-xs { gap: var(--r5-space-1); }
  .gap-sm { gap: var(--r5-space-2); }
  .gap-md { gap: var(--r5-space-3); }
  .gap-lg { gap: var(--r5-space-4); }
  .overflow-hidden { overflow: hidden; }
  .overflow-auto { overflow: auto; }
  .relative { position: relative; }
  .absolute { position: absolute; }
  .inset-0 { inset: 0; }
  .w-full { width: 100%; }
  .h-full { height: 100%; }
`;

export const spacingStyles = css`
  .p-xs { padding: var(--r5-space-1); }
  .p-sm { padding: var(--r5-space-2); }
  .p-md { padding: var(--r5-space-3); }
  .p-lg { padding: var(--r5-space-4); }
  .px-sm { padding-inline: var(--r5-space-2); }
  .px-md { padding-inline: var(--r5-space-3); }
  .py-xs { padding-block: var(--r5-space-1); }
  .py-sm { padding-block: var(--r5-space-2); }
  .m-0 { margin: 0; }
  .mb-sm { margin-bottom: var(--r5-space-2); }
  .mb-md { margin-bottom: var(--r5-space-3); }
`;

export const typographyStyles = css`
  .text-xs { font-size: var(--r5-font-size-badge); }
  .text-sm { font-size: var(--r5-text-xs); }
  .text-base { font-size: var(--r5-text-sm); }
  .text-lg { font-size: var(--r5-text-base); }
  .text-muted { color: var(--r5-text-muted); }
  .text-success { color: var(--r5-success-700); }
  .text-error { color: var(--r5-destructive-700); }
  .text-warning { color: var(--r5-warning-700); }
  .font-medium { font-weight: 500; }
  .font-semibold { font-weight: 600; }
  .truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mono { font-family: var(--r5-font-mono); }
`;

export const decorationStyles = css`
  .border { border: 1px solid var(--r5-border); }
  .border-b { border-bottom: 1px solid var(--r5-border); }
  .border-r { border-right: 1px solid var(--r5-border); }
  .border-l { border-left: 1px solid var(--r5-border); }
  .rounded { border-radius: var(--r5-radius-md); }
  .rounded-sm { border-radius: var(--r5-radius-sm); }
  .rounded-lg { border-radius: var(--r5-radius-lg); }
  .shadow-sm { box-shadow: var(--r5-shadow-sm); }
  .bg-surface { background: var(--r5-surface); }
  .bg-muted { background: var(--r5-surface-muted); }
  .bg-elevated { background: var(--r5-surface-elevated); }
`;

export const interactiveStyles = css`
  .clickable {
    cursor: pointer;
    transition: background var(--r5-duration-fast) var(--r5-ease-default);
  }
  .clickable:hover {
    background: color-mix(in srgb, var(--r5-primary) 8%, transparent);
  }
  .clickable:active {
    background: color-mix(in srgb, var(--r5-primary) 14%, transparent);
  }
`;

export const badgeStyles = css`
  .badge {
    display: inline-flex;
    align-items: center;
    gap: var(--r5-space-1);
    padding: 2px var(--r5-space-2);
    border-radius: var(--r5-radius-full);
    font-size: var(--r5-font-size-badge);
    font-weight: 500;
    line-height: 1.4;
    white-space: nowrap;
  }
  .badge-success {
    background: var(--r5-success-50);
    color: var(--r5-success-700);
    border: 1px solid var(--r5-success-200);
  }
  .badge-error {
    background: var(--r5-destructive-50);
    color: var(--r5-destructive-700);
    border: 1px solid var(--r5-destructive-200);
  }
  .badge-warning {
    background: var(--r5-warning-50);
    color: var(--r5-warning-700);
    border: 1px solid var(--r5-warning-200);
  }
  .badge-info {
    background: var(--r5-primary-50);
    color: var(--r5-primary);
    border: 1px solid var(--r5-primary-200);
  }
  .badge-neutral {
    background: var(--r5-surface-muted);
    color: var(--r5-text-muted);
    border: 1px solid var(--r5-border);
  }
`;

export const panelStyles = css`
  .panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }
  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--r5-space-2) var(--r5-space-3);
    border-bottom: 1px solid var(--r5-border);
    flex-shrink: 0;
  }
  .panel-header h3 {
    margin: 0;
    font-size: var(--r5-text-xs);
    font-weight: 600;
  }
  .panel-body {
    flex: 1;
    overflow: auto;
    padding: var(--r5-space-3);
  }
  .panel-footer {
    display: flex;
    align-items: center;
    gap: var(--r5-space-2);
    padding: var(--r5-space-2) var(--r5-space-3);
    border-top: 1px solid var(--r5-border);
    flex-shrink: 0;
  }
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--r5-space-2);
    padding: var(--r5-space-6);
    color: var(--r5-text-muted);
    text-align: center;
  }
`;

/** Combined export for convenience */
export const allStyles = [
  baseStyles,
  layoutStyles,
  spacingStyles,
  typographyStyles,
  decorationStyles,
  interactiveStyles,
  badgeStyles,
  panelStyles,
];
